import React, { useEffect, useMemo, useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  TextField,
  Autocomplete,
  Chip,
  Button,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { caseService, Case } from '../services/caseService';
import { projectAPI } from '../services/api';
import { firmService } from '../services/firmService';

const statusOptions = ['open', 'in_progress', 'completed', 'closed'];

const Cases: React.FC = () => {
  const { firmId } = useParams<{ firmId?: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [cases, setCases] = useState<Case[]>([]);
  const [projects, setProjects] = useState<{ id: number; name: string }[]>([]);
  const [firmName, setFirmName] = useState<string>('');

  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [fileNumber, setFileNumber] = useState('');
  const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  
  // Pagination state
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(15);
  const [total, setTotal] = useState<number>(0);
  const [lastPage, setLastPage] = useState<number>(1);

  useEffect(() => {
    if (!firmId) return;
    const load = async () => {
      setLoading(true);
      try {
        // Build filter parameters
        const filterParams: any = {
          page,
          per_page: perPage,
        };
        
        // Add filters if they have values
        if (title) filterParams.title = title;
        if (subtitle) filterParams.subtitle = subtitle;
        if (fileNumber) filterParams.file_number = fileNumber;
        if (selectedProjectIds.length > 0) filterParams.project_id = selectedProjectIds;
        if (selectedStatuses.length > 0) filterParams.statuses = selectedStatuses;
        
        const [casesRes, projectsRes] = await Promise.all([
          caseService.getFirmCases(parseInt(firmId), filterParams),
          projectAPI.getAll({ firm_id: firmId }),
        ]);
        
        // Handle paginated response
        if (casesRes.data && casesRes.data.data) {
          setCases(casesRes.data.data);
          setTotal(casesRes.data.total || 0);
          setLastPage(casesRes.data.last_page || 1);
        } else {
          // Fallback for non-paginated response
          const casesList = casesRes.data?.data || casesRes.data || [];
          setCases(Array.isArray(casesList) ? casesList : []);
          setTotal(Array.isArray(casesList) ? casesList.length : 0);
          setLastPage(1);
        }
        
        const projList = projectsRes.data.data?.data || projectsRes.data.data || [];
        setProjects(projList.map((p: any) => ({ id: p.id, name: p.name })));
        try {
          const firmRes = await firmService.getFirmById(parseInt(firmId));
          setFirmName(firmRes.data.name);
        } catch (_e) {
          setFirmName('');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [firmId, page, perPage, title, subtitle, fileNumber, selectedProjectIds, selectedStatuses]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [title, subtitle, fileNumber, selectedProjectIds, selectedStatuses]);
  
  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const handlePerPageChange = (event: any) => {
    setPerPage(parseInt(event.target.value));
    setPage(1);
  };

  if (!firmId) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>Cases</Typography>
        <Paper sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">Select a firm to view Case Tracker.</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          color="inherit"
          href="#"
          onClick={(e) => { e.preventDefault(); navigate('/firms'); }}
          sx={{ textDecoration: 'none' }}
        >
          Firms
        </Link>
        <Link
          color="inherit"
          href="#"
          onClick={(e) => { e.preventDefault(); navigate(`/firms/${firmId}`); }}
          sx={{ textDecoration: 'none' }}
        >
          {firmName || `Firm #${firmId}`}
        </Link>
        <Typography color="text.primary">Case Tracker</Typography>
      </Breadcrumbs>
      <Typography variant="h4" gutterBottom>
        Case Tracker
      </Typography>

      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} flexWrap="wrap" gap={2}>
          <Box flex={{ xs: '1 1 100%', sm: '1 1 220px' }}>
            <TextField label="Title" fullWidth size="small" value={title} onChange={(e) => setTitle(e.target.value)} />
          </Box>
          <Box flex={{ xs: '1 1 100%', sm: '1 1 220px' }}>
            <TextField label="Sub Title" fullWidth size="small" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
          </Box>
          <Box flex={{ xs: '1 1 100%', sm: '1 1 260px' }} minWidth={{ xs: '100%', sm: 240 }}>
            <Autocomplete
              multiple
              size="small"
              options={projects}
              getOptionLabel={(o) => o.name}
              value={projects.filter((p) => selectedProjectIds.includes(p.id))}
              onChange={(_e, vals: { id: number; name: string }[]) => setSelectedProjectIds(vals.map((v) => v.id))}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option.name} {...getTagProps({ index })} key={option.id} size="small" />
                ))
              }
              renderInput={(params) => <TextField {...params} label="Projects" placeholder="Choose some options" />}
            />
          </Box>
          <Box flex={{ xs: '1 1 100%', sm: '1 1 220px' }} minWidth={{ xs: '100%', sm: 240 }}>
            <Autocomplete
              multiple
              size="small"
              options={statusOptions}
              getOptionLabel={(o) => o.replace('_', ' ')}
              value={selectedStatuses}
              onChange={(_e, vals: string[]) => setSelectedStatuses(vals)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option.replace('_', ' ')} {...getTagProps({ index })} key={option} size="small" />
                ))
              }
              renderInput={(params) => <TextField {...params} label="Status" placeholder="Choose some options" />}
            />
          </Box>
          <Box flex={{ xs: '1 1 100%', sm: '1 1 220px' }}>
            <TextField label="File Number" fullWidth size="small" value={fileNumber} onChange={(e) => setFileNumber(e.target.value)} />
          </Box>
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      ) : (
        <TableContainer 
          component={Paper}
          sx={{
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Table size="small" sx={{ minWidth: 600 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>ID</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Title</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>Sub Title</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', sm: 'table-cell' } }}>Project</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Status</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}>File Number</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cases.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>#{c.id}</TableCell>
                  <TableCell>
                    <Typography
                      sx={{ 
                        color: 'primary.main', 
                        cursor: 'pointer', 
                        textDecoration: 'underline',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}
                      onClick={() => navigate(`/firms/${firmId}/cases/${c.id}`)}
                    >
                      {c.title}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{c.subtitle || '—'}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{c.project?.name || '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={String(c.status).replace('_', ' ').toUpperCase()} />
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{c.file_number || '—'}</TableCell>
                </TableRow>
              ))}
              {cases.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">No cases match your filters.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {!loading && total > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Per Page</InputLabel>
              <Select
                value={perPage}
                label="Per Page"
                onChange={handlePerPageChange}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={15}>15</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              Showing {((page - 1) * perPage) + 1} to {Math.min(page * perPage, total)} of {total} cases
            </Typography>
          </Box>
          <Pagination
            count={lastPage}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  );
};

export default Cases; 